import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lightning, Envelope, Lock } from "@phosphor-icons/react";
import { renderMarkdown } from "../lib/markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditHero() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [step, setStep] = useState("idle"); // idle | partial | full
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [email, setEmail] = useState("");

  const startAudit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/audit/start`, { url });
      setAudit(data);
      setStep("partial");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const unlockFull = async (e) => {
    e.preventDefault();
    if (!email.trim() || !audit?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/audit/${audit.id}/unlock`, { email });
      setAudit({ ...audit, full: data.full });
      setStep("full");
      toast.success("Full audit unlocked.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed");
    } finally {
      setLoading(false);
    }
  };

  const goToReport = () => {
    navigate(`/audit/${audit.id}`);
  };

  return (
    <div className="bg-[#F5F5F5] border border-[#0A0A0A]">
      <div className="border-b border-[#0A0A0A] px-6 py-3 flex items-center justify-between bg-[#0A0A0A] text-white">
        <span className="font-mono text-xs uppercase tracking-[0.2em]">FREE SEO SNAPSHOT · NO SIGNUP</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">~15 sec</span>
      </div>

      <div className="p-6 md:p-8">
        {step === "idle" && (
          <form onSubmit={startAudit} data-testid="audit-form">
            <label className="text-sm font-semibold flex items-center gap-2">
              <Lightning size={16} weight="bold" className="text-[#002FA7]" />
              Paste your website URL — get an instant AI audit
            </label>
            <div className="mt-3 flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourcompany.com"
                className="input-line flex-1"
                required
                data-testid="audit-url-input"
              />
              <button type="submit" className="btn-primary justify-center" disabled={loading} data-testid="audit-start-btn">
                {loading ? "Analyzing…" : <>Run free audit <ArrowRight size={16} weight="bold" /></>}
              </button>
            </div>
            <p className="mt-3 text-xs text-[#737373]">
              We'll analyze your URL and return a preview instantly. No signup required.
            </p>
          </form>
        )}

        {step === "partial" && audit && (
          <div data-testid="audit-partial">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#002FA7] mb-2">
              Preview · {audit.url}
            </p>
            <article
              className="prose-tight bg-white p-5 border border-[#E5E7EB]"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(audit.partial) }}
            />
            <div className="mt-5 border border-[#002FA7] bg-white p-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#002FA7] mb-2">
                Step 2 · Unlock the full audit
              </p>
              <h3 className="font-display text-lg font-bold">Get your free SEO snapshot. Upgrade to unlock the full SEO + Ads + Social growth report.</h3>
              <form onSubmit={unlockFull} className="mt-3 flex flex-col md:flex-row gap-2" data-testid="audit-unlock-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="input-line flex-1"
                  required
                  data-testid="audit-email-input"
                />
                <button type="submit" className="btn-primary justify-center" disabled={loading} data-testid="audit-unlock-btn">
                  {loading ? "Unlocking…" : <>Unlock full report <ArrowRight size={16} weight="bold" /></>}
                </button>
              </form>
              <p className="mt-2 text-xs text-[#737373] inline-flex items-center gap-1.5">
                <Lock size={11} weight="bold" /> We'll email you a copy. No spam, unsubscribe anytime.
              </p>
            </div>
          </div>
        )}

        {step === "full" && audit && (
          <div data-testid="audit-full">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#16A34A]">
                ✓ Full audit · {audit.url}
              </p>
              <button onClick={goToReport} className="btn-secondary text-sm" data-testid="audit-view-full-btn">
                Open shareable report <ArrowRight size={14} weight="bold" />
              </button>
            </div>
            <article
              className="prose-tight bg-white p-5 border border-[#E5E7EB] max-h-[520px] overflow-auto"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(audit.full) }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
